import React, { useState, useEffect } from 'react';
import Button from './Button';
import packageContent from '../content/service-packages.json';

/**
 * Contact form component
 * @returns {React.ReactElement} ContactForm component
 */
const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    location: '',
    message: '',
    honeypot: '' // Anti-spam honeypot field
  });
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    submitting: false,
    error: null,
    success: false
  });

  // eslint-disable-next-line no-unused-vars
  const [queryParams, setQueryParams] = useState({});

  // Check URL for query parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const service = params.get('service');
    const packageId = params.get('package');
    const discount = params.get('discount');
    const addons = params.get('addons');
    const realestate_addons = params.get('realestate_addons');
    const bundle = params.get('bundle');
    const volume = params.get('volume');
    
    // Only run this effect once on component mount
    // We're using an empty dependency array with eslint-disable to avoid
    // re-running this effect when formData changes, which would create a loop
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialFormData = { ...formData };
    let updatedQueryParams = {};
    
    // Handle service parameter
    if (service) {
      updatedQueryParams.service = service;
      initialFormData.projectType = service;
      initialFormData.message = `I'm interested in your ${service.replace('-', ' ')} services.`;
    }
    
    // Handle package parameter
    if (packageId) {
      updatedQueryParams.package = packageId;
      
      // Find the actual package title from the JSON file
      let packageTitle = '';
      let packageCategory = '';
      
      // Search through all categories and packages to find the matching package
      packageContent.categories.forEach(category => {
        const foundPackage = category.packages.find(pkg => pkg.id === packageId);
        if (foundPackage) {
          packageTitle = foundPackage.title;
          packageCategory = category.id;
        }
      });
      
      // If package title not found, format it from the ID as fallback
      if (!packageTitle) {
        packageTitle = packageId
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      
      // Set project type based on package category
      if (packageCategory === 'construction') {
        initialFormData.projectType = 'mapping';
      } else if (packageCategory === 'realestate') {
        initialFormData.projectType = 'photography';
      }
      
      // Create message with package information
      initialFormData.message = `I'm interested in the "${packageTitle}" package.`;
            
      // Add request for more information
      initialFormData.message += `\n\nPlease provide me with more information about pricing and availability.`;
      // Add discount note for construction packages
      if (discount === 'true') {
        initialFormData.message += `\n\n(Note: 10% discount for 3-month prepayment.)`;
      }
      
      // Add volume discount note for real estate packages
      if (volume === 'true') {
        initialFormData.message += `\n\n(Note: 10% volume discount for 5+ listings per month.)`;
      }
      
    }
    
    // Handle construction add-ons parameter
    if (addons === 'true') {
      initialFormData.projectType = 'mapping';
      initialFormData.message = `I'm interested in learning more about your optional add-on services for construction projects.\n\nPlease provide information about: `;
      
      // Add bundle discount note if bundle parameter is present
      if (bundle === 'true') {
        initialFormData.message += `\n\n(Note: 10% discount for bundling two or more add-on services.)`;
      }
    }
    
    // Handle real estate add-ons parameter
    if (realestate_addons === 'true') {
      initialFormData.projectType = 'photography';
      initialFormData.message = `I'm interested in learning more about your optional add-on services for real estate listings.\n\nPlease provide information about: `;
      
      // Add bundle discount note if bundle parameter is present
      if (bundle === 'true') {
        initialFormData.message += `\n\n(Note: 10% discount for bundling two or more add-on services.)`;
      }
    }
    
    setQueryParams(updatedQueryParams);
    setFormData(initialFormData);
    // We intentionally only want this to run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const projectTypes = [
    { value: '', label: 'Select a Project Type' },
    { value: 'photography', label: 'Aerial Photography' },
    { value: 'videography', label: 'Aerial Videography' },
    { value: 'mapping', label: 'Mapping & Surveying' },
    { value: 'inspections', label: 'Inspections' },
    { value: 'other', label: 'Other' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Don't submit if honeypot is filled (bot)
    if (formData.honeypot) {
      return;
    }
    
    setFormStatus({
      submitted: false,
      submitting: true,
      error: null,
      success: false
    });
    
    try {
      // Call the Cloudflare Worker endpoint directly
      const apiUrl = process.env.REACT_APP_CONTACT_FORM_API || 'https://lsas-contact-form.lonestaraerialsolutions.workers.dev';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      // Try to parse the response as JSON, but handle the case where it's not valid JSON
      let responseData;
      try {
        const responseText = await response.text();
        
        if (responseText) {
          responseData = JSON.parse(responseText);
        } else {
          throw new Error('Empty response from server');
        }
      } catch (jsonError) {
        console.error('Error parsing JSON:', jsonError);
        throw new Error('Invalid response from server. Please try again later.');
      }
      
      if (!response.ok) {
        throw new Error(responseData?.error || `Server error: ${response.status}`);
      }
      
      // The new worker returns { ok: true } on success
      if (!responseData.ok) {
        throw new Error(responseData.error || 'Failed to submit form');
      }
      
      setFormStatus({
        submitted: true,
        submitting: false,
        error: null,
        success: true
      });
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        location: '',
        message: '',
        honeypot: ''
      });
      
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus({
        submitted: true,
        submitting: false,
        error: error.message || 'Something went wrong. Please try again.',
        success: false
      });
    }
  };
  
  // Success message
  if (formStatus.success) {
    return (
      <div className="bg-green-900/30 border border-green-700 rounded-2xl p-8 text-center">
        <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="text-2xl font-bold text-green-300 mb-2">Message Received!</h3>
        <p className="text-green-400 mb-6">
          Thank you for contacting Lone Star Aerial Solutions. We'll get back to you within 24 hours.
        </p>
        <Button onClick={() => setFormStatus(prev => ({ ...prev, success: false }))}>
          Send Another Message
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-brand-black border border-gray-700 rounded-2xl shadow-card p-8">
      {formStatus.error && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 p-4 rounded-lg mb-6">
          {formStatus.error}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block mb-2 font-medium text-white">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-brand-blue-600 focus:border-brand-blue-600"
          />
        </div>
        
        {/* Email */}
        <div>
          <label htmlFor="email" className="block mb-2 font-medium text-white">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-brand-blue-600 focus:border-brand-blue-600"
          />
        </div>
        
        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block mb-2 font-medium text-white">
            Phone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-brand-blue-600 focus:border-brand-blue-600"
          />
        </div>
        
        {/* Project Type */}
        <div>
          <label htmlFor="projectType" className="block mb-2 font-medium text-white">
            Project Type <span className="text-red-500">*</span>
          </label>
          <select
            id="projectType"
            name="projectType"
            value={formData.projectType}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-brand-blue-600 focus:border-brand-blue-600"
          >
            {projectTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Location */}
      <div className="mb-6">
        <label htmlFor="location" className="block mb-2 font-medium text-white">
          Project Location <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="location"
          name="location"
          value={formData.location}
          onChange={handleChange}
          required
          placeholder="City, TX"
          className="w-full px-4 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-brand-blue-600 focus:border-brand-blue-600"
        />
      </div>
      
      {/* Message */}
      <div className="mb-6">
        <label htmlFor="message" className="block mb-2 font-medium text-white">
          Message <span className="text-red-500">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows="5"
          className="w-full px-4 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-brand-blue-600 focus:border-brand-blue-600"
        ></textarea>
      </div>
      
      {/* Honeypot field (hidden from users, visible to bots) */}
      <div className="hidden">
        <label htmlFor="honeypot">Leave this field empty</label>
        <input
          type="text"
          id="honeypot"
          name="honeypot"
          value={formData.honeypot}
          onChange={handleChange}
          autoComplete="off"
        />
      </div>
      
      <div className="text-center">
        <Button type="submit" disabled={formStatus.submitting}>
          {formStatus.submitting ? 'Sending...' : 'Send Message'}
        </Button>
      </div>
    </form>
  );
};

export default ContactForm;
